import React, { useEffect, useRef, useState } from 'react';
import {
  ContextualMenuItemType,
  IContextualMenuItem,
  IContextualMenuItemRenderFunctions,
  IContextualMenuProps,
  useTheme,
} from '@fluentui/react';
import { ChevronDown12Regular as ChevronIcon } from '@fluentui/react-icons';
import { keyframes } from '@fluentui/style-utilities';
import { IAppTheme } from 'common/theme/AppTheme';

import { BaseButton } from '../BaseButton';
import { IBaseButtonProps } from '../BaseButton.types';
import { IconButton } from '../IconButton';

import { getIconClassName, getStyles } from './MenuButton.styles';

export interface IIOSMenuButtonProps extends IBaseButtonProps {
  menuProps: IContextualMenuProps;
  hideChevron?: boolean;
}

// Define animation once outside component to avoid duplicates
const slideUpAnimation = keyframes({
  from: {
    transform: 'translateY(100%)',
  },
  to: {
    transform: 'translateY(0)',
  },
});

/**
 * iOS-optimized menu button that uses native-like patterns
 * for better iOS voice control accessibility.
 * Uses <button> elements that iOS can properly number and recognize.
 * @param props - The menu button properties
 * @returns {React.FunctionComponent} A button that opens an iOS-optimized menu.
 */
const IOSMenuButton: React.FunctionComponent<IIOSMenuButtonProps> = (props) => {
  const { menuProps, hideChevron, styles, children, ...otherProps } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [menuSize, setMenuSize] = useState({ width: 200, maxHeight: 400 });
  const [showAbove, setShowAbove] = useState(false);
  const [menuBottom, setMenuBottom] = useState<number | undefined>(undefined);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const theme = useTheme() as IAppTheme;
  const customStyles = getStyles(styles, theme, otherProps.componentAs === IconButton);
  const iconClassName = getIconClassName();

  // Detect if device is iPhone (not iPad)
  const isIPhone = /iPhone/.test(navigator.userAgent) && !/iPad/.test(navigator.userAgent);

  const toggleMenu = () => {
    if (!isOpen && buttonRef.current && !isIPhone) {
      // Only calculate position for non-iPhone devices (iPad, desktop, etc.)
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const spacing = 8;
      const minMenuWidth = 200;

      // Calculate available width
      const availableWidth = viewportWidth - spacing * 2;
      const menuWidth = Math.max(minMenuWidth, Math.min(300, availableWidth));

      // Calculate horizontal position (center on mobile if possible)
      let { left } = rect;
      if (viewportWidth < 768) {
        // On mobile, try to center the menu or fit it within viewport
        left = Math.max(spacing, Math.min(rect.left, viewportWidth - menuWidth - spacing));
      } else {
        // On desktop, align to button
        if (left + menuWidth > viewportWidth - spacing) {
          left = Math.max(spacing, rect.right - menuWidth);
        }
        left = Math.max(spacing, left);
      }

      // Calculate vertical position and available space
      const spaceBelow = viewportHeight - rect.bottom - spacing;
      const spaceAbove = rect.top - spacing;

      // Decide whether to show above or below based on available space
      // Show above if there's more space above AND either:
      // 1. Space below is very limited (< 150px), OR
      // 2. Space above is significantly larger (more than 100px difference)
      const shouldShowAbove = spaceAbove > spaceBelow && (spaceBelow < 150 || spaceAbove - spaceBelow > 100);
      setShowAbove(shouldShowAbove);

      // Use available space without arbitrary caps
      const maxMenuHeight = shouldShowAbove ? spaceAbove : spaceBelow;

      // Calculate position based on direction
      const top = shouldShowAbove ? undefined : rect.bottom + spacing;
      const bottom = shouldShowAbove ? viewportHeight - rect.top + spacing : undefined;

      setMenuPosition({
        top: top ?? 0,
        left,
      });

      setMenuSize({
        width: menuWidth,
        maxHeight: maxMenuHeight,
      });

      setMenuBottom(bottom);
    }
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  // Close menu when clicking outside or scrolling
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    const handleScroll = () => {
      closeMenu();
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleMenuItemClick = (item: IContextualMenuItem) => {
    if (item.onClick) {
      item.onClick(undefined, item);
    }
    closeMenu();
  };

  const renderMenuItems = (items: IContextualMenuItem[] | undefined) => {
    if (!items) return null;

    return items.map((item, index) => {
      if (item.itemType === ContextualMenuItemType.Divider) {
        return (
          <div
            key={item.key || `divider-${item.text}-${index}`}
            role="separator"
            style={{
              height: '1px',
              backgroundColor: theme.palette.neutralLight,
              margin: '4px 0',
            }}
          />
        );
      }

      if (item.itemType === ContextualMenuItemType.Header) {
        return (
          <div
            key={item.key || `header-${item.text}-${index}`}
            role="heading"
            aria-level={2}
            style={{
              padding: '8px 16px',
              fontSize: '12px',
              fontWeight: 600,
              color: theme.palette.neutralSecondary,
              textTransform: 'uppercase',
              userSelect: 'none',
            }}
          >
            {item.text}
          </div>
        );
      }

      const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
        const target = e.currentTarget;
        if (!item.disabled) {
          target.style.backgroundColor = theme.palette.neutralLighter;
        }
      };

      const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
        const target = e.currentTarget;
        target.style.backgroundColor = 'transparent';
      };

      return (
        <button
          key={item.key || `item-${index}`}
          type="button"
          disabled={item.disabled}
          onClick={() => handleMenuItemClick(item)}
          aria-label={item.ariaLabel || item.text}
          style={{
            width: '100%',
            padding: '8px 16px',
            border: 'none',
            background: 'transparent',
            textAlign: 'left',
            cursor: item.disabled ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            color: item.disabled ? theme.palette.neutralTertiary : theme.semanticColors.menuItemText,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'background-color 0.1s ease',
            fontFamily: theme.fonts.medium.fontFamily,
            WebkitTapHighlightColor: 'transparent',
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {item.onRenderContent ? (
            // Use custom render function if provided
            item.onRenderContent(
              {
                item,
                classNames: {},
                index,
                hasIcons: items.some((i) => i.iconProps || i.onRenderIcon),
              },
              {} as IContextualMenuItemRenderFunctions
            )
          ) : (
            <>
              {item.onRenderIcon && (
                <span style={{ display: 'flex', alignItems: 'center', minWidth: '20px' }}>
                  {item.onRenderIcon({
                    item,
                    classNames: {},
                    index,
                    hasIcons: items.some((i) => i.iconProps || i.onRenderIcon),
                  })}
                </span>
              )}
              {!item.onRenderIcon && item.iconProps && (
                <span style={{ display: 'flex', alignItems: 'center', minWidth: '20px' }}>
                  <i className={item.iconProps.iconName} />
                </span>
              )}
              <span>{item.text}</span>
            </>
          )}
        </button>
      );
    });
  };

  const MenuIcon = <ChevronIcon className={iconClassName} />;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={buttonRef}>
      <BaseButton
        styles={customStyles}
        allowDisabledFocus
        onClick={toggleMenu}
        onRenderMenuIcon={hideChevron || otherProps.isLoading ? undefined : () => MenuIcon}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        {...otherProps}
      >
        {children}
      </BaseButton>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999998,
              backgroundColor: isIPhone ? 'rgba(0, 0, 0, 0.4)' : 'transparent',
              transition: 'background-color 0.2s ease',
            }}
            onClick={closeMenu}
            aria-hidden="true"
          />

          {/* Menu */}
          <div
            ref={menuRef}
            role="menu"
            aria-label={menuProps.ariaLabel}
            style={{
              position: 'fixed',
              ...(isIPhone
                ? {
                    // iPhone: bottom sheet style
                    bottom: 0,
                    left: 0,
                    right: 0,
                    width: '100%',
                    maxHeight: '60vh',
                    borderRadius: '16px 16px 0 0',
                    animationName: slideUpAnimation,
                    animationDuration: '0.3s',
                    animationTimingFunction: 'ease',
                  }
                : {
                    // iPad and others: contextual menu style
                    ...(showAbove ? { bottom: `${menuBottom}px` } : { top: `${menuPosition.top}px` }),
                    left: `${menuPosition.left}px`,
                    width: `${menuSize.width}px`,
                    maxHeight: `${menuSize.maxHeight}px`,
                    borderRadius: '4px',
                  }),
              backgroundColor: theme.semanticColors.menuBackground,
              border: `1px solid ${theme.semanticColors.menuDivider || theme.palette.neutralLight}`,
              boxShadow: theme.effects.elevation8,
              zIndex: 9999999,
              overflowY: 'auto',
              padding: '4px 0',
              color: theme.semanticColors.menuItemText,
            }}
          >
            {renderMenuItems(menuProps.items)}
          </div>
        </>
      )}
    </div>
  );
};

export default IOSMenuButton;
